import { useEffect, useState } from 'react';
import {
  AUTO_ORDER_NUMBER_CHANNELS,
  EXPENSE_CATEGORIES,
  INCOME_CHANNELS,
  PAYMENT_METHODS,
  formatJMD,
} from '../../lib/finances';
import VendorAutocomplete from '../VendorAutocomplete';
import { todayISO } from '../../lib/dates';


const BLANK = {
  date: todayISO(),
  category: EXPENSE_CATEGORIES[0],
  vendor: '',
  description: '',
  currency: 'JMD',
  amount: '',
  exchangeRate: '',
  notes: '',
  channel: INCOME_CHANNELS[0],
  orderNumber: '',
  paymentMethod: PAYMENT_METHODS[0],
};

export default function EntryForm({ type, editingEntry, vendors, onCreateVendor, onSubmit, onCancelEdit }) {
  const isExpense = type === 'expense';

  const [date, setDate] = useState(BLANK.date);
  const [category, setCategory] = useState(BLANK.category);
  const [vendor, setVendor] = useState(BLANK.vendor);
  const [description, setDescription] = useState(BLANK.description);
  const [currency, setCurrency] = useState(BLANK.currency);
  const [amount, setAmount] = useState(BLANK.amount);
  const [exchangeRate, setExchangeRate] = useState(BLANK.exchangeRate);
  const [notes, setNotes] = useState(BLANK.notes);
  const [channel, setChannel] = useState(BLANK.channel);
  const [orderNumber, setOrderNumber] = useState(BLANK.orderNumber);
  const [paymentMethod, setPaymentMethod] = useState(BLANK.paymentMethod);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editingEntry) {
      setDate(editingEntry.entry_date);
      setCategory(editingEntry.category || BLANK.category);
      setVendor(editingEntry.vendor || '');
      setDescription(editingEntry.description);
      setCurrency(editingEntry.currency);
      setAmount(String(editingEntry.amount));
      setExchangeRate(editingEntry.currency === 'USD' ? String(editingEntry.exchange_rate) : '');
      setNotes(editingEntry.notes || '');
      setChannel(editingEntry.channel || BLANK.channel);
      setOrderNumber(editingEntry.order_number || '');
      setPaymentMethod(editingEntry.payment_method || BLANK.paymentMethod);
    } else {
      setDate(BLANK.date);
      setCategory(BLANK.category);
      setVendor(BLANK.vendor);
      setDescription(BLANK.description);
      setCurrency(BLANK.currency);
      setAmount(BLANK.amount);
      setExchangeRate(BLANK.exchangeRate);
      setNotes(BLANK.notes);
      setChannel(BLANK.channel);
      setOrderNumber(BLANK.orderNumber);
      setPaymentMethod(BLANK.paymentMethod);
    }
    setError(null);
  }, [editingEntry]);

  const isAutoOrderNumber = !isExpense && AUTO_ORDER_NUMBER_CHANNELS.includes(channel);

  const numericAmount = Number(amount || 0);
  const numericRate = currency === 'USD' ? Number(exchangeRate || 0) : 1;
  const jmdEquivalent = numericAmount * numericRate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (currency === 'USD' && numericRate <= 0) {
      setError('Enter the exchange rate used for this purchase.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        id: editingEntry?.id,
        entry_date: date,
        ...(isExpense
          ? { category, vendor: vendor || null }
          : { channel, order_number: orderNumber || null, payment_method: paymentMethod }),
        description,
        currency,
        amount: numericAmount,
        exchange_rate: numericRate,
        notes: notes || null,
      });
      if (!editingEntry) {
        setDescription('');
        setVendor('');
        setAmount('');
        setExchangeRate('');
        setNotes('');
        setOrderNumber('');
      }
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="entry-form" onSubmit={handleSubmit}>
      <div className="entry-form-grid">
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>

        {isExpense && (
          <label>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="entry-form-wide">
          {isExpense ? 'Description' : 'Description / customer'}
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </label>

        {isExpense && (
          <label>
            Vendor (optional)
            <VendorAutocomplete
              value={vendor}
              onChange={setVendor}
              vendors={vendors}
              onCreateVendor={onCreateVendor}
            />
          </label>
        )}

        {!isExpense && (
          <>
            <label>
              Channel
              <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                {INCOME_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Order #
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                disabled={isAutoOrderNumber}
                placeholder={isAutoOrderNumber ? 'Auto-generated on save' : ''}
              />
            </label>

            <label>
              Payment method
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="JMD">JMD</option>
            <option value="USD">USD</option>
          </select>
        </label>

        <label>
          Amount ({currency})
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>

        {currency === 'USD' && (
          <label>
            Exchange rate (JMD per USD)
            <input
              type="number"
              min="0"
              step="0.0001"
              value={exchangeRate}
              onChange={(e) => setExchangeRate(e.target.value)}
              required
            />
          </label>
        )}

        <label className="entry-form-wide">
          Notes (optional)
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      {currency === 'USD' && numericAmount > 0 && (
        <p className="entry-form-jmd-preview">JMD equivalent: {formatJMD(jmdEquivalent)}</p>
      )}

      {error && <p className="entry-form-error">{error}</p>}

      <div className="entry-form-actions">
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : editingEntry ? 'Save changes' : isExpense ? 'Add expense' : 'Add income'}
        </button>
        {editingEntry && (
          <button type="button" className="entry-form-cancel" onClick={onCancelEdit}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
